import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import StoryComparison from '../components/StoryComparison';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import LoadingScreen from '../components/LoadingScreen';
import { loadDataset } from '../utils/dataLoader';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const HomePage = () => {
    const query = useQuery();
    const navigate = useNavigate();
    const [selectedModel, setSelectedModel] = useState('');
    const [selectedPremise, setSelectedPremise] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [selectedNumber, setSelectedNumber] = useState(0);
    const [dataset, setDataset] = useState(null);
    const [showDifferences, setShowDifferences] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true); 
            const data = await loadDataset();
            setDataset(data);

            const urlModel = query.get('llm');
            const urlPremise = query.get('premise');
            const urlType = query.get('type');
            const urlStory = parseInt(query.get('story'));

            if (urlModel) 
                setSelectedModel(urlModel);
            if (urlPremise) 
                setSelectedPremise(urlPremise);
            if (urlType) 
                setSelectedType(urlType);
            if (!isNaN(urlStory)) 
                setSelectedNumber(urlStory);

            setIsLoading(false);
        };
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const factualStory = getStory('factual');
        if (factualStory && factualStory.title) {
            document.title = `${factualStory.title} - Unveiling Gender Bias in LLM-Generated Hero and Heroine Narratives`;
        } else {
            document.title = 'Unveiling Gender Bias in LLM-Generated Hero and Heroine Narratives';
        }
    }, [selectedModel, selectedPremise, selectedType, selectedNumber, dataset]);

    const updateUrl = (model, premise, type, story) => {
        const params = new URLSearchParams();
        if (model) params.set('llm', model);
        if (premise) params.set('premise', premise);
        if (type) params.set('type', type);
        if (story !== undefined) params.set('story', story);
        navigate(`/?${params.toString()}`);
    };

    const handleModelChange = (e) => {
        const newModel = e.target.value;
        setSelectedModel(newModel);
        updateUrl(newModel, selectedPremise, selectedType, selectedNumber);
    };

    const handlePremiseChange = (e) => {
        const newPremise = e.target.value;
        setSelectedPremise(newPremise);
        updateUrl(selectedModel, newPremise, selectedType, selectedNumber);
    };

    const handleTypeChange = (e) => {
        const newType = e.target.value;
        setSelectedType(newType);
        updateUrl(selectedModel, selectedPremise, newType, selectedNumber);
    };

    const handleStorySelect = (index) => {
        setSelectedNumber(index);
        updateUrl(selectedModel, selectedPremise, selectedType, index);
    };

    const getModelNames = () => {
        return dataset ? Object.keys(dataset.models) : [];
    };

    const getPremiseNames = () => {
        if (!dataset || !selectedModel) return [];
        return Object.keys(dataset.models[selectedModel]);
    };

    const getStoryTypes = () => {
        if (!dataset || !selectedModel || !selectedPremise) return [];
        return Object.keys(dataset.models[selectedModel][selectedPremise]);
    };

    const getStoryTitles = () => {
        if (!dataset || !selectedModel || !selectedPremise || !selectedType) return [];
        const factualStories = dataset.models[selectedModel][selectedPremise][selectedType]?.factual || [];
        return factualStories.map((story, index) => ({
            index,
            title: story.title || `Story ${index + 1}`,
        }));
    };

    const getStory = (version) => {
        if (!dataset || !selectedModel || !selectedPremise || !selectedType) return null;
        const stories = dataset.models[selectedModel][selectedPremise][selectedType]?.[version];
        return stories ? stories[selectedNumber] : null;
    };

    const handleToggleDifferences = () => {
        setShowDifferences((prev) => !prev);
    };

    if (isLoading) {
        return <LoadingScreen />;
    }

    return (
        <Box sx={{ width: '100vw', height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <TopBar title="Unveiling Gender Bias in LLM-Generated Hero and Heroine Narratives" />
            <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                <Sidebar
                    selectedModel={selectedModel}
                    selectedPremise={selectedPremise}
                    selectedType={selectedType}
                    selectedNumber={selectedNumber}
                    modelNames={getModelNames()}
                    premiseNames={getPremiseNames()}
                    storyTypes={getStoryTypes()}
                    storyTitles={getStoryTitles()}
                    showDifferences={showDifferences}
                    onToggleDifferences={handleToggleDifferences}
                    onModelChange={handleModelChange}
                    onPremiseChange={handlePremiseChange}
                    onTypeChange={handleTypeChange}
                    onStorySelect={handleStorySelect}
                />

                <Box 
                    sx={{ 
                        flexGrow: 1, 
                        overflowY: 'auto', 
                        padding: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backgroundColor: '#ffffff',
                        height: 'calc(100vh - 64px)'  
                    }}
                >
                    <Box sx={{ width: '100%' }}>
                        <StoryComparison
                            factualStory={getStory('factual')}
                            counterfactualStory={getStory('counterfactual')}
                            showDifferences={showDifferences}
                            modelName={selectedModel}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default HomePage;
